import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { TransferService } from "./transfer.service";

const initialState = {
  transfers: [],
  pagination: null,
  transfer: null,
  isLoading: false,
  success: false,
  error: null,
  message: null,
};

export const createTransfer = createAsyncThunk(
  "transfers/createTransfer",
  async (data, { rejectWithValue }) => {
    try {
      const _service = new TransferService();
      const response = await _service.createTransfer(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getTransfers = createAsyncThunk(
  "transfers/getTransfers",
  async (params, { rejectWithValue }) => {
    try {
      const _service = new TransferService();
      const response = await _service.getTransfers(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getTransfer = createAsyncThunk(
  "transfers/getTransfer",
  async (id, { rejectWithValue }) => {
    try {
      const _service = new TransferService();
      const response = await _service.getTransfer(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const receiveTransfer = createAsyncThunk(
  "transfers/receiveTransfer",
  async (id, { rejectWithValue }) => {
    try {
      const _service = new TransferService();
      return await _service.receiveTransfer(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const cancelTransfer = createAsyncThunk(
  "transfers/cancelTransfer",
  async (id, { rejectWithValue }) => {
    try {
      const _service = new TransferService();
      return await _service.cancelTransfer(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const transferSlice = createSlice({
  name: "transfers",
  initialState,
  reducers: {
    reset: (state) => {
      state.success = false;
      state.isLoading = false;
      state.error = null;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    // createTransfer
    builder.addCase(createTransfer.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createTransfer.fulfilled, (state, action) => {
      state.isLoading = false;
      state.success = true;
      state.message = action.payload.message;
      state.transfers.unshift(action.payload.data);
    });
    builder.addCase(createTransfer.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // getTransfers
    builder.addCase(getTransfers.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getTransfers.fulfilled, (state, action) => {
      state.isLoading = false;
      state.transfers = action.payload.data;
      state.pagination = action.payload.pagination;
    });
    builder.addCase(getTransfers.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // getTransfer
    builder.addCase(getTransfer.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getTransfer.fulfilled, (state, action) => {
      state.isLoading = false;
      state.transfer = action.payload.data;
    });
    builder.addCase(getTransfer.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // receive / cancel — reflect new status on the matching row
    const applyStatus = (state, action) => {
      state.isLoading = false;
      state.success = true;
      state.message = action.payload.message;
      const updated = action.payload.data;
      if (updated?.id != null) {
        const row = state.transfers.find((tr) => tr.id === updated.id);
        if (row) row.status = updated.status;
        if (state.transfer?.id === updated.id) state.transfer.status = updated.status;
      }
    };
    builder.addCase(receiveTransfer.pending, (state) => { state.isLoading = true; });
    builder.addCase(receiveTransfer.fulfilled, applyStatus);
    builder.addCase(receiveTransfer.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; });
    builder.addCase(cancelTransfer.pending, (state) => { state.isLoading = true; });
    builder.addCase(cancelTransfer.fulfilled, applyStatus);
    builder.addCase(cancelTransfer.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; });
  },
});

export const { reset } = transferSlice.actions;
export default transferSlice.reducer;
